// src/services/circuitBreaker.js
// Circuit breaker for external API calls.
//
// Three states:
//   CLOSED  — normal operation, requests pass through
//   OPEN    — too many failures, requests rejected immediately
//   HALF_OPEN — testing if the service recovered
//
// Prevents hammering failed services (NOAA, NWS, DOTD, Entergy).
// Each service gets its own breaker instance.
//
// Usage:
//   const noaaBreaker = getBreaker('noaa');
//   const data = await noaaBreaker.call(() => fetchNOAAData());
//
// If the breaker is OPEN, call() throws CircuitOpenError immediately
// and the caller should serve cached data instead.

const BREAKER_STORAGE_KEY = 'eos-circuit-breakers';
const DEFAULT_CONFIG = {
  failureThreshold:   3,      // open after 3 consecutive failures
  successThreshold:   2,      // close after 2 consecutive successes in HALF_OPEN
  halfOpenTimeoutMs:  30 * 60_000, // 60s before testing again after OPEN
  retryDelayMs:       30_000,  // 5s between retries within a window
};

const STATES = { CLOSED: 'CLOSED', OPEN: 'OPEN', HALF_OPEN: 'HALF_OPEN' };

// ── Persistent state via localStorage ─────────────────────────────
// Survives page reloads — if NOAA was down, don't immediately hammer
// on next page load.
function loadBreakers() {
  try {
    return JSON.parse(localStorage.getItem(BREAKER_STORAGE_KEY) ?? '{}');
  } catch { return {}; }
}

function saveBreakers(data) {
  try { localStorage.setItem(BREAKER_STORAGE_KEY, JSON.stringify(data)); } catch {}
}

// In-memory registry of circuit breaker instances
const _instances = new Map();

class CircuitBreaker {
  constructor(name, config = {}) {
    this.name   = name;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this._state = this._loadState();
  }

  _loadState() {
    const all = loadBreakers();
    return all[this.name] ?? {
      state:            STATES.CLOSED,
      failures:         0,
      successes:        0,
      lastFailureTime:  null,
      lastAttemptTime:  null,
    };
  }

  _saveState() {
    const all = loadBreakers();
    all[this.name] = this._state;
    saveBreakers(all);
  }

  get state() {
    // Check if OPEN breaker should transition to HALF_OPEN
    if (this._state.state === STATES.OPEN) {
      const elapsed = Date.now() - (this._state.lastFailureTime ?? 0);
      if (elapsed >= this.config.halfOpenTimeoutMs) {
        this._state.state = STATES.HALF_OPEN;
        this._state.successes = 0;
        this._saveState();
      }
    }
    return this._state.state;
  }

  isAvailable() { return this.state !== STATES.OPEN; }

  /**
   * Execute fn through the circuit breaker.
   * Throws CircuitOpenError if the circuit is OPEN.
   * Records success/failure and updates state.
   */
  async call(fn) {
    if (this.state === STATES.OPEN) {
      const waitMs = Math.max(0,
        this.config.halfOpenTimeoutMs - (Date.now() - (this._state.lastFailureTime ?? 0))
      );
      throw Object.assign(
        new Error(`Circuit breaker OPEN for ${this.name}. Retry in ${Math.ceil(waitMs / 1000)}s`),
        { name: 'CircuitOpenError', waitMs }
      );
    }

    this._state.lastAttemptTime = Date.now();

    try {
      const result = this._onSuccess(await fn());
      return result;
    } catch (err) {
      if (err.name === 'CircuitOpenError') throw err;
      this._onFailure(err);
      throw err;
    }
  }

  _onSuccess(result) {
    if (this._state.state === STATES.HALF_OPEN) {
      this._state.successes += 1;
      if (this._state.successes >= this.config.successThreshold) {
        // Service recovered — close the circuit
        this._state.state    = STATES.CLOSED;
        this._state.failures = 0;
        console.info(`[CircuitBreaker] ${this.name}: HALF_OPEN → CLOSED (recovered)`);
      }
    } else {
      // Reset failure count on success
      this._state.failures = 0;
    }
    this._saveState();
    return result;
  }

  _onFailure(err) {
    this._state.failures       += 1;
    this._state.lastFailureTime = Date.now();

    const msg = `[CircuitBreaker] ${this.name}: failure ${this._state.failures}/${this.config.failureThreshold} — ${err.message}`;

    if (this._state.state === STATES.HALF_OPEN) {
      // Failed during probe — back to OPEN
      this._state.state = STATES.OPEN;
      console.warn(`${msg} → OPEN (probe failed)`);
    } else if (this._state.failures >= this.config.failureThreshold) {
      this._state.state = STATES.OPEN;
      console.warn(`${msg} → OPEN (threshold reached)`);
    } else {
      console.warn(msg);
    }

    this._saveState();
  }

  /** Force-reset the breaker (admin/debug use) */
  reset() {
    this._state = { state: STATES.CLOSED, failures: 0, successes: 0,
                    lastFailureTime: null, lastAttemptTime: null };
    this._saveState();
  }

  /** Summary for memory monitor */
  status() {
    return { name: this.name, state: this._state.state,
             failures: this._state.failures,
             lastFailure: this._state.lastFailureTime };
  }
}

/** Get or create a circuit breaker for a named service */
export function getBreaker(name, config = {}) {
  if (!_instances.has(name)) {
    _instances.set(name, new CircuitBreaker(name, config));
  }
  return _instances.get(name);
}

/** All active breakers (for memory monitor) */
export function getAllBreakers() {
  return [..._instances.values()].map(b => b.status());
}

/** Named breakers for all external services */
export const BREAKERS = {
  nws:     getBreaker('nws',     { failureThreshold: 3 }),
  noaa:    getBreaker('noaa',    { failureThreshold: 3 }),
  usgs:    getBreaker('usgs',    { failureThreshold: 3 }),
  dotd:    getBreaker('dotd',    { failureThreshold: 3 }),
  entergy: getBreaker('entergy', { failureThreshold: 3 }),
  gohsep:  getBreaker('gohsep',  { failureThreshold: 3 }),
};
